import { useMemo, useState } from 'react';
import { AlertCircle, Clock, Zap, Target, Activity, Waypoints } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { cn } from '../../lib/utils';

import type { ProcessedEvent } from './inspector-context';

interface PerformancePanelProps {
  events: ProcessedEvent[]; // (StoredCaptureEvent | SpanEvent)
  startAt: number;
  durationMs: number;
  capabilityColors: Record<string, string>;
  onSelect?: ((idx: number) => void) | undefined;
}

export function PerformancePanel({
  events,
  startAt,
  durationMs,
  capabilityColors,
  onSelect,
}: PerformancePanelProps): import('react/jsx-runtime').JSX.Element {
  const [hoveredEventId, setHoveredEventId] = useState<string | null>(null);

  // Focus only on events that actually carry duration measurements
  const validEvents = useMemo(() => {
    return events
      .map((e, idx) => ({ ...e, _originalIndex: idx }))
      .filter(
        (e): boolean =>
          (e.phase === 'response' || e.phase === 'error' || e.phase === 'span') &&
          (e.durationMs ?? 0) > 0,
      );
  }, [events]);

  const {
    stats,
    computeTime,
    ioWaitTime,
    slowestSpan,
    insight,
    actionableFix,
    slowestSpans,
    criticalPath,
    isParallel,
    isOptimizationCandidate,
    simulatedMs,
  } = useMemo(() => {
    // 1. Capability Stats & Time Accumulation
    const statsMap = new Map<
      string,
      {
        calls: number;
        totalMs: number;
        spans: typeof validEvents;
        capability: string;
        action: string;
        fnName?: string | undefined;
      }
    >();
    let ioWaitTime = 0;
    const IO_CAPABILITIES = new Set(['fetch', 'db', 'checkpoint', 'rpc', 'http']); // Broaden IO list

    // To calculate Critical Path: sort events by start time.
    let currentEndTime = 0;
    const path: typeof validEvents = [];
    let maxOverlapCount = 0;

    const getStartTime = (e: (typeof validEvents)[0]): number =>
      e.phase === 'response' || e.phase === 'error' ? e.at - e.durationMs! : e.at;

    const sortedByStart = [...validEvents].sort(
      (a, b): number => getStartTime(a) - getStartTime(b),
    );

    // Parallelism check: If another span starts before currentEndTime.
    for (let i = 0; i < sortedByStart.length; i++) {
      const e = sortedByStart[i];
      const duration = e!.durationMs!;
      const start = getStartTime(e!);
      const end = start + duration;

      for (let j = i + 1; j < sortedByStart.length; j++) {
        if (getStartTime(sortedByStart[j]!) < end) {
          maxOverlapCount++;
        } else {
          break;
        }
      }

      // Stats
      const capKey = e!.fnName ?? `${e!.capability}.${e!.action}`;
      const existing = statsMap.get(capKey) ?? {
        calls: 0,
        totalMs: 0,
        spans: [],
        capability: e!.capability,
        action: e!.action,
        fnName: e!.fnName,
      };
      existing.calls++;
      existing.totalMs += duration;
      existing.spans.push(e!);
      statsMap.set(capKey, existing);

      // IO grouping
      if (IO_CAPABILITIES.has(e!.capability)) {
        ioWaitTime += duration;
      }

      if (start >= currentEndTime || (end > currentEndTime && duration > 50)) {
        path.push(e!);
        currentEndTime = Math.max(currentEndTime, end);
      }
    }

    const computeTime = Math.max(0, durationMs - ioWaitTime);

    // Compute top bottleneck capability
    const statsArray = Array.from(statsMap.entries())
      .map(([key, data]) => ({
        key,
        capability: data.capability,
        action: data.action,
        fnName: data.fnName,
        calls: data.calls,
        totalMs: data.totalMs,
        avgMs: Math.round(data.totalMs / data.calls),
        pct: durationMs > 0 ? (data.totalMs / durationMs) * 100 : 0,
        spans: data.spans.sort((a, b): number => (b.durationMs ?? 0) - (a.durationMs ?? 0)),
      }))
      .sort((a, b): number => b.totalMs - a.totalMs);

    const bottleneckCap = statsArray[0] ?? null;

    // Highest individual bottleneck (grouped)
    const groupedBottlenecks = statsArray.filter(
      (s): boolean => s.totalMs > durationMs * 0.15 || s.totalMs > 100,
    );

    const slowestSpansRaw = [...validEvents].sort(
      (a, b): number => (b.durationMs ?? 0) - (a.durationMs ?? 0),
    );
    const slowestSpan = slowestSpansRaw[0] ?? null;

    const isParallel = maxOverlapCount > 0;

    const criticalPathSig = path
      .filter((p): boolean => slowestSpan == null || p.durationMs! > slowestSpan.durationMs! * 0.1)
      .slice(0, 5);

    // Insight generation
    let insightTxt = 'Execution is strictly sequential.';
    let actionableFix = '';
    let isOptimizationCandidate = false;
    let simulatedMs = durationMs;

    const totalIoCalls = validEvents.filter((e): boolean =>
      IO_CAPABILITIES.has(e.capability),
    ).length;

    if (isParallel) {
      insightTxt = 'Execution shows strong parallelism.';
      if (bottleneckCap != null && bottleneckCap.pct > 40) {
        insightTxt =
          '⚠️ Parallel, but heavily constrained by ' +
          (bottleneckCap.fnName ?? bottleneckCap.capability) +
          ' calls.';
        actionableFix = 'Cache results if possible or batch requests.';
      }
    } else {
      // Sequential insight logic
      if (totalIoCalls > 1 && ioWaitTime > 100) {
        const maxIoSingle = Math.max(
          ...validEvents
            .filter((e): boolean => IO_CAPABILITIES.has(e.capability))
            .map((e): number => e.durationMs ?? 0),
        );
        simulatedMs = computeTime + maxIoSingle;
        isOptimizationCandidate = simulatedMs < durationMs * 0.8; // At least 20% savings

        insightTxt = `Execution is fully sequential.\n${totalIoCalls} I/O calls executed one after another → Causing ${Math.round(ioWaitTime)}ms total latency.`;

        if (isOptimizationCandidate) {
          actionableFix = `If parallelized → expected ~${Math.round(simulatedMs)}ms`;
        } else if (bottleneckCap != null && bottleneckCap.pct > 40) {
          actionableFix = `Optimize ${bottleneckCap.fnName ?? bottleneckCap.capability} loop or move to a background task.`;
        }
      } else if (bottleneckCap != null && bottleneckCap.pct > 40) {
        insightTxt =
          '⚠️ Compute-heavy execution tied up by ' +
          (bottleneckCap.fnName ?? bottleneckCap.capability) +
          '.';
        actionableFix = 'Optimize loop or offload to a background task.';
      }
    }

    return {
      stats: statsArray,
      computeTime,
      ioWaitTime,
      slowestSpan,
      bottleneckCap,
      insight: insightTxt,
      actionableFix,
      isOptimizationCandidate,
      simulatedMs,
      slowestSpans: groupedBottlenecks.slice(0, 4),
      criticalPath: criticalPathSig,
      isParallel,
    };
  }, [validEvents, durationMs]);

  const getThresholdLabel = (ms: number): import('react/jsx-runtime').JSX.Element | null => {
    if (ms > 1000)
      return (
        <span className="bg-red-500/20 text-red-500 px-1.5 py-0.5 rounded text-[10px] uppercase font-bold">
          Critical
        </span>
      );
    if (ms > 500)
      return (
        <span className="bg-orange-500/20 text-orange-500 px-1.5 py-0.5 rounded text-[10px] uppercase font-bold">
          Slow
        </span>
      );
    return null;
  };

  const getSpanType = (cap: string): 'network/IO' | 'compute' => {
    return ['fetch', 'db', 'checkpoint', 'rpc', 'http'].includes(cap) ? 'network/IO' : 'compute';
  };

  const handleSelect = (origIdx: number): void => {
    if (onSelect) onSelect(origIdx);
  };

  return (
    <div className="p-4 space-y-8 bg-black/5">
      {/* 1. Performance Summary */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-emerald-400" />
          <h3 className="text-sm font-semibold uppercase tracking-wider">Performance Summary</h3>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500 to-indigo-500 opacity-50" />

          <div className="flex flex-col">
            <span className="text-[11px] text-muted-foreground uppercase mb-1">Total Time</span>
            <span className="text-xl tabular-nums font-mono">
              {(durationMs / 1000).toFixed(2)}s{' '}
              <span className="text-sm text-muted-foreground">({durationMs}ms)</span>
            </span>
          </div>

          <div className="flex flex-col">
            <span className="text-[11px] text-muted-foreground uppercase mb-1">Slowest Span</span>
            <div className="text-sm tabular-nums font-mono flex items-center gap-2">
              {slowestSpan != null ? (
                <button
                  onClick={(): void => handleSelect(slowestSpan._originalIndex)}
                  className="flex items-center gap-2 hover:bg-white/10 p-1 rounded transition-colors text-left text-orange-400"
                >
                  <span>
                    {slowestSpan.fnName ?? `${slowestSpan.capability}.${slowestSpan.action}`}
                  </span>
                  <span className="text-muted-foreground/70">({slowestSpan.durationMs}ms)</span>
                </button>
              ) : (
                '-'
              )}
            </div>
          </div>

          <div className="flex flex-col md:col-span-2 border-t border-white/5 pt-3">
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <span className="text-[11px] text-muted-foreground uppercase mb-0.5 block">
                  Concurrency
                </span>
                <span className="text-sm font-medium">
                  {isParallel ? 'Parallel' : 'Sequential'}
                </span>
                {!isParallel && isOptimizationCandidate && (
                  <div className="mt-2 text-[10px] text-blue-400 bg-blue-500/10 px-2 py-1 rounded inline-block">
                    Potential Savings: ~{durationMs - (simulatedMs || 0)}ms
                  </div>
                )}
              </div>
              <div className="flex-1 rounded p-2.5 bg-blue-500/10 border border-blue-500/20 text-blue-100 flex gap-2 items-start">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-blue-400" />
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase font-bold tracking-widest text-blue-400/80 mb-0.5">
                    Insight
                  </span>
                  <span className="text-xs leading-relaxed">{insight}</span>
                  {actionableFix && (
                    <span className="text-xs text-emerald-300 mt-1 flex items-center gap-1.5">
                      <Target className="w-3 h-3" /> {actionableFix}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Bottlenecks (Grouped) */}
      {slowestSpans.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-orange-400" />
            <h3 className="text-sm font-semibold uppercase tracking-wider">Bottlenecks</h3>
          </div>
          <div className="space-y-2">
            {slowestSpans.map((group, idx): import('react/jsx-runtime').JSX.Element => {
              const isNetwork = getSpanType(group.capability) === 'network/IO';
              const label = isNetwork ? 'External call latency' : 'Compute bottleneck';
              const biggestSpan = group.spans[0];

              return (
                <div
                  key={'bottle-' + group.capability + '-' + idx}
                  className={cn(
                    'flex items-center justify-between p-2.5 rounded border transition-colors cursor-pointer',
                    hoveredEventId === biggestSpan?.id
                      ? 'bg-white/10 border-white/20'
                      : 'bg-white/5 border-white/5 hover:bg-white/10',
                  )}
                  onMouseEnter={(): void => setHoveredEventId(biggestSpan?.id ?? null)}
                  onMouseLeave={(): void => setHoveredEventId(null)}
                  onClick={(): void => {
                    if (biggestSpan != null) handleSelect(biggestSpan._originalIndex);
                  }}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground w-4">{idx + 1}.</span>
                    <span className="text-sm font-mono font-medium">
                      {biggestSpan?.fnName ?? `${group.capability}.${biggestSpan?.action}`}{' '}
                      {group.calls > 1 ? '(' + group.calls + ')' : ''}
                    </span>
                    {getThresholdLabel(group.totalMs)}
                  </div>
                  <div className="flex items-center justify-end gap-4">
                    <span className="text-[10px] text-muted-foreground uppercase">{label}</span>
                    <span className="text-orange-400 font-mono tabular-nums text-right w-12">
                      {group.totalMs}ms
                    </span>
                    <span className="text-muted-foreground font-mono tabular-nums text-right w-10 text-xs">
                      ({Math.round(group.pct)}%)
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* 3. Critical Path */}
      {criticalPath.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Waypoints className="w-4 h-4 text-purple-400" />
            <h3 className="text-sm font-semibold uppercase tracking-wider">Critical Path</h3>
          </div>
          <div className="bg-white/5 rounded-lg border border-white/10 p-4 font-mono text-xs overflow-x-auto">
            <div className="flex items-center gap-2 whitespace-nowrap min-w-max">
              {criticalPath.map((span, i): import('react/jsx-runtime').JSX.Element => (
                <div key={'cp-' + i} className="flex items-center gap-2">
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <button
                          className="px-2.5 py-1.5 bg-purple-500/10 border border-purple-500/30 rounded text-purple-200 cursor-pointer hover:bg-purple-500/20"
                          onMouseEnter={(): void => setHoveredEventId(span.id)}
                          onMouseLeave={(): void => setHoveredEventId(null)}
                          onClick={(): void => handleSelect(span._originalIndex)}
                        />
                      }
                    >
                      {span.fnName ?? `${span.capability}.${span.action}`}{' '}
                      <span className="text-purple-400/60 ml-1">({span.durationMs}ms)</span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <div className="text-[10px]">
                        Starts at: +
                        {Math.round(
                          span.phase === 'span'
                            ? span.at - startAt
                            : span.at - startAt - span.durationMs!,
                        )}
                        ms
                      </div>
                    </TooltipContent>
                  </Tooltip>

                  {i < criticalPath.length - 1 && (
                    <span className="text-muted-foreground mx-1">→</span>
                  )}
                  {i === criticalPath.length - 1 && (
                    <>
                      <span className="text-muted-foreground mx-1">→</span>
                      <span className="text-muted-foreground italic">return</span>
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-3 text-muted-foreground text-[11px] pt-3 border-t border-white/5">
              Total execution path bound:{' '}
              <strong className="text-foreground">
                {criticalPath.reduce((acc, s): number => acc + s.durationMs!, 0)}ms
              </strong>
            </div>
          </div>
        </section>
      )}

      {/* 4. Execution Waterfall */}
      {validEvents.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-blue-400" />
            <h3 className="text-sm font-semibold uppercase tracking-wider">Execution Waterfall</h3>
          </div>
          <div className="bg-black/20 border border-white/5 rounded p-3 pt-6 space-y-1.5 relative overflow-x-hidden">
            <div className="absolute top-1 left-28 right-0 pr-4 flex justify-between text-[9px] text-muted-foreground/40 font-mono pointer-events-none">
              <span>0s</span>
              <span>
                {durationMs > 1000
                  ? (durationMs / 2000).toFixed(2) + 's'
                  : Math.round(durationMs / 2) + 'ms'}
              </span>
              <span>
                {durationMs > 1000 ? (durationMs / 1000).toFixed(2) + 's' : durationMs + 'ms'}
              </span>
            </div>

            {validEvents.map((event, mapIdx): import('react/jsx-runtime').JSX.Element => {
              const isEndtime = event.phase === 'response' || event.phase === 'error';
              const startTime = isEndtime ? event.at - event.durationMs! : event.at;
              const offset = Math.round(startTime - startAt);
              const barLeft = durationMs > 0 ? Math.max(0, (offset / durationMs) * 100) : 0;
              const barWidth =
                durationMs > 0 ? Math.max((event.durationMs! / durationMs) * 100, 0.5) : 100;
              const color = capabilityColors[event.capability] ?? 'var(--flux-console)';
              const isHovered = hoveredEventId === event.id;

              return (
                <Tooltip key={event.id + '-' + mapIdx}>
                  <TooltipTrigger
                    render={
                      <button
                        className={cn(
                          'group flex items-center h-6 hover:bg-white/10 rounded cursor-pointer w-full text-left transition-colors',
                          isHovered ? 'bg-white/10' : '',
                        )}
                        onMouseEnter={(): void => setHoveredEventId(event.id)}
                        onMouseLeave={(): void => setHoveredEventId(null)}
                        onClick={(): void => handleSelect(event._originalIndex)}
                        data-testid={'waterfall-bar-' + event.capability + '.' + event.action}
                      />
                    }
                  >
                    <span
                      className="w-40 shrink-0 truncate text-[11px] font-mono text-muted-foreground mr-2 text-right group-hover:text-foreground block"
                      title={event.fnName ?? `${event.capability}.${event.action}`}
                    >
                      {event.fnName ?? `${event.capability}.${event.action}`}
                    </span>
                    <div className="flex-1 relative h-3 bg-transparent overflow-hidden rounded-sm flex items-center">
                      <div
                        className="absolute top-0 bottom-0 rounded-sm opacity-80 group-hover:opacity-100 transition-opacity"
                        style={{
                          left: `${barLeft}%`,
                          width: `${barWidth}%`,
                          backgroundColor: color,
                          minWidth: '2px',
                        }}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="flex flex-col gap-1 w-full max-w-sm">
                      <div className="flex items-center justify-between gap-4 font-mono mb-1">
                        <span className="font-bold flex items-center gap-1.5" style={{ color }}>
                          <Zap className="w-3 h-3" />{' '}
                          {event.fnName ?? `${event.capability}.${event.action}`}
                        </span>
                        <span className="text-orange-400">{event.durationMs}ms</span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-[11px] border-t border-white/10 pt-2">
                        <div className="text-muted-foreground">Start:</div>
                        <div>+{offset}ms</div>
                        <div className="text-muted-foreground">End:</div>
                        <div>+{offset + (event.durationMs ?? 0)}ms</div>
                        {event.url != null && (
                          <>
                            <div className="text-muted-foreground">URL:</div>
                            <div className="truncate max-w-[200px]" title={event.url}>
                              {String(event.url)}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </section>
      )}

      {/* 5. Breakdown */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-4 h-4 text-yellow-400" />
          <h3 className="text-sm font-semibold uppercase tracking-wider">Time Breakdown</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white/5 border border-white/5 rounded-lg p-3">
            <span className="text-[10px] text-muted-foreground uppercase block mb-3">
              Resource Type
            </span>
            <div className="flex h-4 rounded overflow-hidden mb-2">
              <div
                className="bg-sky-500"
                style={{ width: `${(computeTime / durationMs) * 100}%` }}
                title="Compute"
              />
              <div
                className="bg-fuchsia-500"
                style={{ width: `${(ioWaitTime / durationMs) * 100}%` }}
                title="I/O & Network"
              />
            </div>
            <div className="flex justify-between text-xs mt-2 font-mono">
              <span className="text-sky-400">Compute: {computeTime}ms</span>
              <span className="text-fuchsia-400">Wait: {ioWaitTime}ms</span>
            </div>
          </div>

          <div className="bg-white/5 border border-white/5 rounded-lg p-3">
            <span className="text-[10px] text-muted-foreground uppercase block mb-3">
              By Capability
            </span>
            <div className="flex flex-col gap-1.5">
              {stats.map((stat, i): import('react/jsx-runtime').JSX.Element => (
                <div key={'stat-' + i} className="flex items-center gap-2 text-[11px] font-mono">
                  <div className="flex-1 overflow-hidden h-2.5 bg-black/40 rounded">
                    <div
                      className="h-full"
                      style={{
                        width: `${stat.pct}%`,
                        backgroundColor: capabilityColors[stat.capability] ?? '#fff',
                      }}
                    />
                  </div>
                  <span
                    className="w-20 text-right truncate"
                    style={{ color: capabilityColors[stat.capability] ?? '#fff' }}
                    title={stat.key}
                  >
                    {stat.fnName ?? stat.capability}
                  </span>
                  <span className="w-10 text-right text-muted-foreground">{stat.calls}x</span>
                  <span className="w-12 text-right">{stat.totalMs}ms</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
